import { useCallback, useEffect, useRef, useState } from 'react';

const defaultOptions = {
  stepRatio: 0.7,
  minStep: 100,
  deps: [],
};

/**
 * Hook for horizontal scroll containers: overflow state and programmatic scroll.
 * Use overscroll-behavior-x: none on the scroll container in CSS to prevent Safari overscroll.
 * @param {Object} options
 * @param {number} [options.stepRatio=0.7] - Scroll step = clientWidth * stepRatio.
 * @param {number} [options.minStep=100] - Minimum scroll step in px.
 * @param {Array} [options.deps=[]] - When these change, effect re-runs (re-measure).
 * @returns {{ scrollRef: React.RefObject, scrollState: { hasOverflow: boolean, canScrollLeft: boolean, canScrollRight: boolean }, scrollBy: (direction: -1|1) => void }}
 */
const useHorizontalScrollState = (options = {}) => {
  const {
    stepRatio = defaultOptions.stepRatio,
    minStep = defaultOptions.minStep,
    deps = defaultOptions.deps,
  } = options;

  const scrollRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    hasOverflow: false,
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollWidth, scrollLeft, clientWidth } = el;
    const hasOverflow = scrollWidth > clientWidth;
    const canScrollLeft = scrollLeft > 1;
    const maxScrollLeft = scrollWidth - clientWidth;
    const canScrollRight = scrollLeft < maxScrollLeft - 1;
    setScrollState((prev) =>
      prev.hasOverflow !== hasOverflow ||
      prev.canScrollLeft !== canScrollLeft ||
      prev.canScrollRight !== canScrollRight
        ? { hasOverflow, canScrollLeft, canScrollRight }
        : prev
    );
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || window.isSSR) return;

    const el = scrollRef.current;
    if (!el) return;

    el.scrollLeft = 0;
    updateState();
    const afterLayoutMeasureId = setTimeout(updateState, 0);
    el.addEventListener('scroll', updateState);
    window.addEventListener('resize', updateState);

    return () => {
      clearTimeout(afterLayoutMeasureId);
      el.removeEventListener('scroll', updateState);
      window.removeEventListener('resize', updateState);
    };
  }, [updateState, ...deps]);

  const scrollBy = useCallback(
    (direction) => {
      const el = scrollRef.current;
      if (!el) return;
      const { scrollWidth, scrollLeft, clientWidth } = el;
      const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
      const step = Math.max(clientWidth * stepRatio, minStep);
      const targetScrollLeft = scrollLeft + direction * step;
      const clampedLeft = Math.max(
        0,
        Math.min(targetScrollLeft, maxScrollLeft)
      );
      el.scrollTo({ left: clampedLeft, behavior: 'smooth' });
    },
    [stepRatio, minStep]
  );

  return { scrollRef, scrollState, scrollBy };
};

export default useHorizontalScrollState;
